import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Award, Copy, TrendingUp, Clock, CheckCircle, DollarSign, CreditCard, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatMoney = (n) => {
  const v = Math.round(Number(n) || 0);
  return v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 ? 1 : 0).replace(/\.0$/, '')}k` : `$${v}`;
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-md flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">{label}</p>
    </div>
    <p className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}</p>
  </div>
);

const VipDashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/vip/dashboard`, { withCredentials: true });
      setData(data);
    } catch (err) { console.error(err); }
  };

  const copyCode = () => {
    if (data?.promo_code) {
      navigator.clipboard.writeText(data.promo_code);
      toast.success('Promo code copied');
    }
  };

  if (!data) return <div className="text-[#A3A3A3]">Loading...</div>;

  const t = data.totals;
  const card = data.savings_card;
  const cardUrl = card && data.vip_id ? `${API}/vips/${data.vip_id}/card?t=${card.uploaded_at || ''}` : '';

  return (
    <div data-testid="vip-dashboard" className="space-y-8">
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-2">VIP Referral Member</p>
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-2">Your <span className="text-[#D4AF37]">Referral Network</span></h2>
        <p className="text-[#A3A3A3]">Share your code. Earn rewards on every fully-paid invoice.</p>
      </div>

      {/* Inactive notice */}
      {data.vip_status === 'inactive' && (
        <div data-testid="vip-inactive-banner" className="bg-red-900/30 border border-red-700/60 rounded-md p-4 text-red-200 text-sm">
          Your VIP membership is currently <strong>inactive</strong>. Contact NexFlow Plumbing to reactivate referral payouts.
        </div>
      )}

      {/* Promo code highlight */}
      <div className="bg-gradient-to-r from-[#141414] via-[#1a1505] to-[#141414] border-2 border-[#D4AF37] rounded-md p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-[#A3A3A3] text-xs uppercase tracking-[0.3em] mb-1">Your Active Promo Code</p>
          <p data-testid="vip-promo-code" className="text-[#D4AF37] text-4xl font-bold font-mono">{data.promo_code || '—'}</p>
          <p className="text-[#A3A3A3] text-xs mt-2">Status: <span className={data.promo_active ? 'text-green-400' : 'text-red-400'}>{data.promo_active ? 'ACTIVE' : 'INACTIVE'}</span></p>
        </div>
        <button
          data-testid="vip-copy-code"
          onClick={copyCode}
          className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 rounded-md font-medium hover:bg-[#F3E5AB] flex items-center gap-2"
        >
          <Copy className="w-4 h-4" /> Copy Code
        </button>
      </div>

      {/* Savings card */}
      {card && (
        <div data-testid="vip-savings-card" className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase">Your VIP Savings Card</p>
            </div>
            <a
              data-testid="vip-card-download"
              href={cardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white"
            >
              <Download className="w-3.5 h-3.5" /> Download / Share
            </a>
          </div>
          {(card.content_type || '').startsWith('image/') ? (
            <img src={cardUrl} alt="Your VIP savings card" className="w-full max-h-80 object-contain rounded-md bg-black border border-[#262626]" />
          ) : (
            <a href={cardUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] hover:border-[#D4AF37]/50 rounded-md p-4 transition-colors">
              <FileText className="w-10 h-10 text-[#D4AF37]" />
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{card.filename}</p>
                <p className="text-[#525252] text-xs">{Math.round((card.size || 0) / 1024)} KB · Tap to open</p>
              </div>
            </a>
          )}
        </div>
      )}

      {/* Live bonus tier reference — pulled from admin settings */}
      <div data-testid="vip-bonus-tiers" className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase">Bonus Tiers</p>
          <p className="text-[#525252] text-[10px] uppercase tracking-wider">{data.program_settings?.discount_percent || 10}% discount to your customers</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {(data.program_settings?.tiers || []).map((tier, idx) => {
            const min = tier.min || 0;
            const max = tier.max;
            const payout = tier.payout || 0;
            const label = max == null
              ? `Invoice ${formatMoney(min)}+`
              : min === 0
                ? `Invoice under ${formatMoney(max + 0.01)}`
                : `${formatMoney(min)} – ${formatMoney(max)}`;
            return (
              <div key={`tier-${idx}-${min}-${max}`} className="bg-[#0A0A0A] border border-[#1f1f1f] rounded-md p-3">
                <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">{label}</p>
                <p className="text-white text-2xl font-bold mt-1">${payout}</p>
              </div>
            );
          })}
        </div>
        {data.program_settings?.allow_stacking && (
          <p className="text-emerald-400 text-[10px] mt-3">✓ Stacking enabled — bonus paid on every qualifying invoice.</p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Award} label="Total Referrals" value={t.total_referrals} />
        <StatCard icon={DollarSign} label="Total Earned" value={`$${t.total_earned.toFixed(2)}`} accent="text-[#D4AF37]" />
        <StatCard icon={CheckCircle} label="Total Paid Out" value={`$${t.paid_total.toFixed(2)}`} accent="text-green-400" />
        <StatCard icon={TrendingUp} label="Conversion Rate" value={`${t.conversion_rate}%`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-[#D4AF37]" /><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Pending Bonuses</p></div>
          <p className="text-3xl font-bold text-[#D4AF37]" data-testid="vip-pending-total">${t.pending_total.toFixed(2)}</p>
          <p className="text-[#525252] text-xs mt-1">{t.pending_count} referral(s) awaiting payment</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><CheckCircle className="w-4 h-4 text-emerald-400" /><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Approved (Owed To You)</p></div>
          <p className="text-3xl font-bold text-emerald-400" data-testid="vip-approved-total">${t.approved_total.toFixed(2)}</p>
          <p className="text-[#525252] text-xs mt-1">{t.approved_count} earned but unpaid</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-green-400" /><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Paid Out</p></div>
          <p className="text-3xl font-bold text-green-400" data-testid="vip-paid-total">${t.paid_total.toFixed(2)}</p>
          <p className="text-[#525252] text-xs mt-1">{t.paid_count} payment(s) sent</p>
        </div>
      </div>
    </div>
  );
};

export default VipDashboard;
